/**
 Copyright (c) 2021, Oracle and/or its affiliates.
 The Universal Permissive License (UPL), Version 1.0
 */

'use strict';

define('vb/components/oj-vb-fragment-param/fragmentParamViewModel',[
  'vb/private/log',
  'ojs/ojcomponentcore',
  'ojs/ojcustomelement-utils',
], (Log, ojComponentCore, { CustomElementUtils }) => {
  /** @typedef {import('ojs/ojcomposite').PropertyChangedContext} PropertyChangedContext */

  const logger = Log.getLogger('/vb/components/oj-vb-fragment-param');
  const findParentFragment = (node) => {
    let parent = node.parentNode;
    let parentFragmentId;
    while (parent) {
      if (parent.nodeName === 'OJ-VB-FRAGMENT') {
        parentFragmentId = parent.getAttribute('id');
        parent = null;
      } else {
        parent = parent.parentNode;
      }
    }
    return parentFragmentId;
  };

  /**
   * ViewModel representing an individual fragment param component - <oj-vb-fragment-param>.
   * @category Component
   */
  class FragmentParamViewModel {
    constructor(context) {
      this.composite = context.element;
      this.properties = context.properties;
      this.parentFragmentId = context.element.parentElement.getAttribute('id');
      this.name = context.properties.name;
      this.log = logger;
    }

    /**
     * Calls updateParam on the parent fragment CCA when it is ready to accept methods.
     * @param {Element} parentFragmentComp parent oj-vb-fragment element
     * @param {*} value param value
     */
    updateParentParam(parentFragmentComp, value) {
      const updateParam = () => {
        if (parentFragmentComp.isConnected) {
          ojComponentCore.callComponentMethod(parentFragmentComp, 'updateParam', this.name, { value });
        }
      };
      // The parent fragment method proxy can exist before its view model is ready.
      // Wait for JET creation when available; otherwise preserve the old synchronous behavior.
      const state = CustomElementUtils.getElementState(parentFragmentComp);
      const createdPromise = state && state.GetCreatedPromise && state.GetCreatedPromise();
      const isComplete = state && state.isComplete && state.isComplete();
      if (createdPromise && !isComplete) {
        createdPromise.then(updateParam, () => {});
      } else {
        updateParam();
      }
    }

    /**
     * Called when a param properties on this composite is updated. Set the params on the parent fragment CCA when
     * this happens.
     * @param {PropertyChangedContext} context
     */
    propertyChanged(context) {
      const { previousValue, value, updatedFrom } = context;
      // internal changes are those that originate within fragment and can be ignored
      if (context.property === 'value' && updatedFrom === 'external') {
        // TODO: should we diff the value? It might be expensive!
        // TODO: name of the param has changed. Notify parent. Maybe throttle changes
        // compare parentProps.params with what is coming in
        const parentFragmentComp = document.getElementById(this.getParentFragmentId());

        // for some reason even after fragment CCA has been disconnected, when bound variable value changes,
        //  propertyChanged is called. This is a side effect of ko observables not being unwound as the entire
        //  fragment CCA can be reattached. In such cases guard against parent fragment DOM not being discoverable!
        if (parentFragmentComp) {
          this.updateParentParam(parentFragmentComp, value);
        }
      } else if (context.property === 'name' && previousValue !== value) {
        // we do not allow the name of the param to be changed on the fly
        this.log.error('changing the name of the param from', previousValue, 'to newValue', value, 'is not supported!');
      }
    }

    /**
     * called when the value of the fragment variable changes. This calls properties.setProperty to flag this write
     * as internal
     * @param {string} name
     * @param {any} value
     */
    valueChangedProxy(name, value) {
      if (name) {
        this.properties.setProperty(name, value);
      }
    }

    getParentFragmentId() {
      if (!this.parentFragmentId) {
        // for some weird reason in the case of fragment used in layout field templates or could be new behavior in
        // JET 12 the fragment-param component models get created before the parent fragment. So we attempt to
        // locate the parent fragment and its id lazily
        this.parentFragmentId = findParentFragment(this.composite);
        if (!this.parentFragmentId) {
          this.log.warn('An id was not available on parent fragment component for the fragment param', this.name);
        }
      }

      return this.parentFragmentId;
    }
  }

  return FragmentParamViewModel;
});

